import React, { FC, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import _get from 'lodash/get';

import {
  FullTalentQuestionDTO,
  FullTalentQuestionDTOType,
  OrganizationDTOTalentUniqueFieldsItem,
  TalentQuestionsResponseDTOTranslations,
  useGetOrganizationBySlug
} from '@/api/generated';
import InfoIcon from '@/assets/icons/circle-info.svg?react';
import PencilIcon from '@/assets/icons/pencil.svg?react';
import AdditionalInfoQuestion from '@/components/AdditionalInfoQuestion';
import ChipSelect from '@/components/fields/ChipSelect';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { useReadonly } from '@/hooks/useReadonly';
import { TALENT_REGISTRATION_FORM_TABS } from '@/root/linker';
import typography from '@/theme/typography';
import { DEFAULT_LANGUAGE_CODE } from '@/views/TalentRegistrationForm/constants';

import TalentRegistrationForm from './TalentRegistrationForm';

interface RegistrationTabTooltipProps {
  children: string;
  isInsideDialog: boolean;
}

const RegistrationTabTooltip = ({ children, isInsideDialog }: RegistrationTabTooltipProps) => {
  const { spacing, palette } = useTheme();

  return (
    <StandardPopper
      {...(isInsideDialog && {
        getSx: () => ({ zIndex: 2000 }),
        treeRootSelector: '.MuiDialog-container'
      })}
      placement="bottom-end"
      caretEndPosition={spacing(1)}
      hoverable
      content={
        <Paper
          sx={{
            mt: 1,
            p: spacing(0.75, 1.5),
            position: 'relative',
            maxWidth: '240px',
            '.StandardPopper-root &': {
              bgcolor: palette.primary[100]
            }
          }}
        >
          <Box>
            <Typography sx={typography.body3}>{children}</Typography>
          </Box>
        </Paper>
      }
    >
      <IconButton
        sx={{
          p: 0,
          width: spacing(2),
          height: spacing(2),
          '& .InfoIcon-root': { width: spacing(2), height: spacing(2) }
        }}
      >
        <InfoIcon className="InfoIcon-root" />
      </IconButton>
    </StandardPopper>
  );
};

interface TalentRegistrationTabProps {
  toggleFullScreenMode?: (isFullScreen: boolean) => void;
  isFullScreenMode?: boolean;
  onTalentRegistrationFormOpen?: () => void;
  isCreateMode?: boolean;
  organizationSlug?: string;
}

const TalentRegistrationTab: FC<TalentRegistrationTabProps> = ({
  isFullScreenMode,
  toggleFullScreenMode,
  onTalentRegistrationFormOpen,
  isCreateMode,
  organizationSlug
}) => {
  const { isReadonly } = useReadonly();
  const { t } = useTranslation(['settings', 'common', 'talents']);
  const [mode, setMode] = useState<TALENT_REGISTRATION_FORM_TABS>(TALENT_REGISTRATION_FORM_TABS.EXTERNAL);
  const { getPrefixedFieldName } = useFieldPrefix();
  const { watch } = useFormContext();
  const questions: FullTalentQuestionDTO[] | null = watch(getPrefixedFieldName(mode));
  const translations: TalentQuestionsResponseDTOTranslations | null = watch(getPrefixedFieldName('translations'));

  const { data: organization } = useGetOrganizationBySlug(
    organizationSlug!,
    { isFull: true },
    { query: { enabled: !!organizationSlug } }
  );

  const { idNumberIsIdentity, passportIsIdentity, phoneIsIdentity } = useMemo(
    () => ({
      idNumberIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.ID_NUMBER),
      passportIsIdentity: organization?.talentUniqueFields?.includes(
        OrganizationDTOTalentUniqueFieldsItem.PASSPORT_NUMBER
      ),
      phoneIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.PHONE)
    }),
    [organization?.talentUniqueFields]
  );

  const externalIdentityFields = useMemo(
    () =>
      mode === TALENT_REGISTRATION_FORM_TABS.EXTERNAL
        ? [
            {
              id: 'firstName',
              type: FullTalentQuestionDTOType.TEXT,
              isRequired: true,
              isTalentEditable: true,
              isActive: true,
              isKnockout: false
            },
            {
              id: 'lastName',
              type: FullTalentQuestionDTOType.TEXT,
              isRequired: true,
              isTalentEditable: true,
              isActive: true,
              isKnockout: false
            },
            {
              id: 'idNumber',
              type: FullTalentQuestionDTOType.TEXT,
              isRequired: idNumberIsIdentity ?? false,
              isTalentEditable: true,
              isActive: true,
              isKnockout: false
            },
            {
              id: 'phone',
              type: FullTalentQuestionDTOType.TEXT,
              isRequired: phoneIsIdentity ?? false,
              isTalentEditable: true,
              isActive: true,
              isKnockout: false
            },
            {
              id: 'passportNumber',
              type: FullTalentQuestionDTOType.TEXT,
              isRequired: passportIsIdentity ?? false,
              isTalentEditable: true,
              isActive: true,
              isKnockout: false
            },
            {
              id: 'email',
              type: FullTalentQuestionDTOType.TEXT,
              isRequired: true,
              isTalentEditable: true,
              isActive: true,
              isKnockout: false
            }
          ]
        : [],
    [mode, idNumberIsIdentity, phoneIsIdentity, passportIsIdentity]
  );

  const onRegistrationTypeChange = useCallback((type: string | number | null) => {
    setMode((type as TALENT_REGISTRATION_FORM_TABS) || TALENT_REGISTRATION_FORM_TABS.EXTERNAL);
  }, []);

  const onEditForm = useCallback(
    () => (onTalentRegistrationFormOpen ? onTalentRegistrationFormOpen() : toggleFullScreenMode?.(true)),
    [onTalentRegistrationFormOpen, toggleFullScreenMode]
  );

  return (
    <Stack
      width="100%"
      height="100%"
      sx={{
        position: 'relative',
        '& .MuiFormLabel-root': { textAlign: 'start' },
        '& .MuiFormControl-root': { p: 0 }
      }}
    >
      <Stack alignItems="start" gap={3} py={3} display={!isFullScreenMode ? 'flex' : 'none'}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Stack direction="row" alignItems="center" gap={1}>
            <ChipSelect
              name="registrationType"
              color="primary"
              options={[
                {
                  value: 'external',
                  label: (
                    <Stack direction="row" alignItems="center" gap={1}>
                      {t('processSettings.form.talentRegistration.tabs.external')}
                      <RegistrationTabTooltip isInsideDialog={!isCreateMode}>
                        {t('processSettings.form.talentRegistration.tooltips.external')}
                      </RegistrationTabTooltip>
                    </Stack>
                  )
                },
                {
                  value: 'internal',
                  label: (
                    <Stack direction="row" alignItems="center" gap={1}>
                      {t('processSettings.form.talentRegistration.tabs.internal')}
                      <RegistrationTabTooltip isInsideDialog={!isCreateMode}>
                        {t('processSettings.form.talentRegistration.tooltips.internal')}
                      </RegistrationTabTooltip>
                    </Stack>
                  )
                }
              ]}
              onChange={onRegistrationTypeChange}
              value={mode}
              enforcedValue
            />
          </Stack>
          <Button
            onClick={onEditForm}
            variant="outlined"
            startIcon={<PencilIcon />}
            sx={{
              ...typography.body3,
              fontWeight: typography.fontWeightMedium,
              py: 0.25
            }}
            disabled={isReadonly}
          >
            {t('processSettings.form.talentRegistration.editFormButton')}
          </Button>
        </Stack>
        {mode === TALENT_REGISTRATION_FORM_TABS.EXTERNAL &&
          externalIdentityFields.map(field => (
            <AdditionalInfoQuestion
              disabled={true}
              key={field.id}
              translation={{
                label: t(`talents:layouts.talentEditModal.fields.${field.id}`)
              }}
              talentQuestion={field as FullTalentQuestionDTO}
            />
          ))}
        {questions
          ?.filter(question => question.isActive)
          ?.map(question => (
            <AdditionalInfoQuestion
              disabled={true}
              key={question?.id}
              translation={_get(translations, `${DEFAULT_LANGUAGE_CODE}.${question?.id}`)}
              talentQuestion={question}
            />
          ))}
      </Stack>
      {isFullScreenMode && (
        <TalentRegistrationForm toggleFullScreenMode={toggleFullScreenMode} organizationSlug={organizationSlug} />
      )}
    </Stack>
  );
};

export default TalentRegistrationTab;
